import { useEffect, useState } from 'react';
import { Clock3, Loader2, Undo2 } from 'lucide-react';
import { Button } from './ui/button';
import { usePendingSends } from '../context/PendingSendContext';

const GRACE_PERIOD_MS = 10000;

export const UndoSendBanner = () => {
  const { pendingSends, undoPendingSend, undoingIds } = usePendingSends();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (pendingSends.length === 0) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(interval);
  }, [pendingSends.length]);

  if (pendingSends.length === 0) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-[140] sm:left-auto sm:right-5 sm:w-[390px] space-y-2" data-testid="undo-send-banner-stack" aria-live="assertive">
      {pendingSends.map(item => {
        const remaining = Math.max(0, new Date(item.undo_until || item.send_at).getTime() - now);
        const seconds = Math.max(0, Math.ceil(remaining / 1000));
        const progress = Math.min(100, Math.max(0, (remaining / GRACE_PERIOD_MS) * 100));
        const undoing = undoingIds.includes(item.id);
        const canUndo = item.status === 'pending_undo' && remaining > 0 && !undoing;
        return (
          <section key={item.id} className="overflow-hidden rounded-lg border border-[#B8D8D1] bg-white shadow-2xl" data-testid={`undo-send-banner-${item.id}`}>
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#E1F1EE] text-[#176C62]">
                <Clock3 className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground" data-testid={`undo-send-countdown-${item.id}`}>
                  {item.status === 'sending' || seconds === 0 ? 'Sending now…' : `Sending in ${seconds}s`}
                </p>
                <p className="truncate text-xs text-muted-foreground">Reply to {item.recipients?.[0] || 'recipient'}</p>
              </div>
              <Button
                type="button"
                size="sm"
                className="rounded-full bg-[#183F3A] text-white hover:bg-[#245A53]"
                disabled={!canUndo}
                onClick={() => undoPendingSend(item)}
                data-testid={`undo-send-button-${item.id}`}
              >
                {undoing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Undo2 className="mr-1.5 h-4 w-4" />}
                Undo
              </Button>
            </div>
            <div className="h-1 bg-[#E7EFEC]">
              <div className="h-full bg-[#2D8B7D] transition-[width] duration-100 ease-linear" style={{ width: `${progress}%` }} />
            </div>
          </section>
        );
      })}
    </div>
  );
};